import React, { useState, useRef } from 'react';
import {
  Star,
  Tv,
  Film,
  Tv2,
  Play,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Rows,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { SeriesPoster } from '../common/SeriesPoster';
import { extractReleaseYear } from '../../utils/m3uParser';
import { shouldHideCategoryFromList, shouldHideContentFromList } from '../../utils/security';

const getDisplayYear = (title: string, year?: number) => {
  if (year && year >= 1920 && year <= 2025) return year;
  const extracted = extractReleaseYear(title);
  if (extracted >= 1920 && extracted <= 2026) return extracted;
  return year && year >= 1920 && year <= 2026 ? year : undefined;
};

export const FavoritesView: React.FC = () => {
  const {
    channels,
    movies,
    series,
    currentProfile,
    parentalConfig,
    setCurrentChannel,
    setCurrentMovie,
    setActiveChannelCategory,
    setActiveMovieCategory,
    setActiveSeriesDetail,
    setActiveTab,
    toggleFavorite,
    toggleCategoryFavorite,
  } = useIPTV();

  const [layoutMode, setLayoutMode] = useState<'carousel' | 'grid'>('carousel');

  // Scroll Container Refs
  const categoriesScrollRef = useRef<HTMLDivElement>(null);
  const channelsScrollRef = useRef<HTMLDivElement>(null);
  const moviesScrollRef = useRef<HTMLDivElement>(null);
  const seriesScrollRef = useRef<HTMLDivElement>(null);

  const scrollContainer = (ref: React.RefObject<HTMLDivElement | null>, direction: 'left' | 'right') => {
    if (ref.current) {
      const scrollAmount = direction === 'left' ? -380 : 380;
      ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const favLiveCategories = (currentProfile.favoriteCategories?.live || []).filter(
    (cat) => !shouldHideCategoryFromList(cat, currentProfile, parentalConfig)
  );
  const favMovieCategories = (currentProfile.favoriteCategories?.movies || []).filter(
    (cat) => !shouldHideCategoryFromList(cat, currentProfile, parentalConfig)
  );
  const favSeriesCategories = (currentProfile.favoriteCategories?.series || []).filter(
    (cat) => !shouldHideCategoryFromList(cat, currentProfile, parentalConfig)
  );

  const favChannelList = channels.filter(
    (c) => currentProfile.favorites?.includes(c.id) && !shouldHideContentFromList(c, currentProfile, parentalConfig)
  );
  const favMovieList = movies.filter(
    (m) => currentProfile.favorites?.includes(m.id) && !shouldHideContentFromList(m, currentProfile, parentalConfig)
  );
  const favSeriesList = series.filter(
    (s) => currentProfile.favorites?.includes(s.id) && !shouldHideContentFromList(s, currentProfile, parentalConfig)
  );

  const totalFavCategories = favLiveCategories.length + favMovieCategories.length + favSeriesCategories.length;
  const totalFavs = favChannelList.length + favMovieList.length + favSeriesList.length + totalFavCategories;

  if (totalFavs === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-950 p-6 text-center">
        <div className="w-16 h-16 rounded-3xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-amber-400 mb-4 shadow-lg">
          <Star className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-black text-white">Nenhum Favorito Adicionado</h3>
        <p className="text-xs text-zinc-400 max-w-sm mt-1 mb-6">
          Marque canais, filmes, séries ou categorias inteiras com a estrela ⭐ para acessá-los rapidamente aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-zinc-950 p-4 md:p-6 overflow-y-auto space-y-8 scrollbar-thin scrollbar-thumb-zinc-800">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-zinc-800">
        <div>
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" /> Meus Favoritos ({currentProfile.name})
          </h2>
          <p className="text-xs text-zinc-400">
            Acesso rápido aos seus conteúdos prediletos • Use as setas ◄ ► do controle remoto para navegar
          </p>
        </div>

        {/* Layout Switcher */}
        <div className="flex items-center gap-1.5 bg-zinc-900/80 p-1 rounded-2xl border border-zinc-800 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setLayoutMode('carousel')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition cursor-pointer ${
              layoutMode === 'carousel'
                ? 'bg-amber-500 text-black font-black shadow-md shadow-amber-500/20'
                : 'text-zinc-400 hover:text-white'
            }`}
            title="Visualização em Linhas / Carrossel com Navegação"
          >
            <Rows className="w-3.5 h-3.5" />
            <span>Em Linha</span>
          </button>

          <button
            type="button"
            onClick={() => setLayoutMode('grid')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition cursor-pointer ${
              layoutMode === 'grid'
                ? 'bg-amber-500 text-black font-black shadow-md shadow-amber-500/20'
                : 'text-zinc-400 hover:text-white'
            }`}
            title="Visualização em Grade Completa (Grid)"
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            <span>Grade</span>
          </button>
        </div>
      </div>

      {/* 1. Categorias Favoritas */}
      {totalFavCategories > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" /> Categorias Favoritas ({totalFavCategories})
            </h3>
            {layoutMode === 'carousel' && totalFavCategories > 3 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => scrollContainer(categoriesScrollRef, 'left')}
                  className="p-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition cursor-pointer shadow flex items-center justify-center"
                  title="Rolar categorias para a esquerda (Controle Remoto ◄)"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollContainer(categoriesScrollRef, 'right')}
                  className="p-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition cursor-pointer shadow flex items-center justify-center"
                  title="Rolar categorias para a direita (Controle Remoto ►)"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="relative group">
            {layoutMode === 'carousel' && totalFavCategories > 4 && (
              <>
                <button
                  type="button"
                  onClick={() => scrollContainer(categoriesScrollRef, 'left')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-r-2xl bg-black/80 hover:bg-zinc-900 text-white border-y border-r border-zinc-700 shadow-2xl transition cursor-pointer hidden md:flex items-center justify-center backdrop-blur-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollContainer(categoriesScrollRef, 'right')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-l-2xl bg-black/80 hover:bg-zinc-900 text-white border-y border-l border-zinc-700 shadow-2xl transition cursor-pointer hidden md:flex items-center justify-center backdrop-blur-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}

            <div
              ref={categoriesScrollRef}
              className={
                layoutMode === 'carousel'
                  ? 'flex items-center gap-3 overflow-x-auto pb-2 scrollbar-none scroll-smooth px-1'
                  : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'
              }
            >
              {/* Live TV Favorite Categories */}
              {favLiveCategories.map((cat, idx) => {
                const count = channels.filter((c) => c.groupTitle === cat).length;
                return (
                  <div
                    key={`fav-cat-live-${cat}`}
                    id={`fav-cat-live-${idx}`}
                    tabIndex={0}
                    role="button"
                    onClick={() => {
                      setActiveChannelCategory(cat);
                      setActiveTab('live');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setActiveChannelCategory(cat);
                        setActiveTab('live');
                      } else if (e.key === 'ArrowRight' && layoutMode === 'carousel') {
                        e.preventDefault();
                        const next = document.getElementById(`fav-cat-live-${idx + 1}`) || document.getElementById(`fav-cat-movies-0`);
                        next?.focus();
                        next?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      } else if (e.key === 'ArrowLeft' && idx > 0 && layoutMode === 'carousel') {
                        e.preventDefault();
                        const prev = document.getElementById(`fav-cat-live-${idx - 1}`);
                        prev?.focus();
                        prev?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      }
                    }}
                    className={`p-3.5 rounded-2xl bg-zinc-900/80 hover:bg-zinc-850 border border-amber-500/30 hover:border-amber-400 transition cursor-pointer flex items-center justify-between group focus:ring-4 focus:ring-amber-400 focus:outline-none ${
                      layoutMode === 'carousel' ? 'w-64 shrink-0' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                        <Tv className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] font-black text-theme-accent uppercase tracking-wider">Ao Vivo</span>
                        <h4 className="text-xs font-bold text-white truncate group-hover:text-amber-300">{cat}</h4>
                        <span className="text-[10px] text-zinc-400">{count} canais</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      title="Remover categoria dos favoritos"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategoryFavorite('live', cat);
                      }}
                      className="p-1.5 text-amber-400 hover:text-zinc-500 transition cursor-pointer"
                    >
                      <Star className="w-4 h-4 fill-amber-400" />
                    </button>
                  </div>
                );
              })}

              {/* Movies Favorite Categories */}
              {favMovieCategories.map((cat, idx) => {
                const count = movies.filter((m) => m.category === cat).length;
                return (
                  <div
                    key={`fav-cat-movies-${cat}`}
                    id={`fav-cat-movies-${idx}`}
                    tabIndex={0}
                    role="button"
                    onClick={() => {
                      setActiveMovieCategory(cat);
                      setActiveTab('movies');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setActiveMovieCategory(cat);
                        setActiveTab('movies');
                      } else if (e.key === 'ArrowRight' && layoutMode === 'carousel') {
                        e.preventDefault();
                        const next = document.getElementById(`fav-cat-movies-${idx + 1}`) || document.getElementById(`fav-cat-series-0`);
                        next?.focus();
                        next?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      } else if (e.key === 'ArrowLeft' && layoutMode === 'carousel') {
                        e.preventDefault();
                        const prev = idx > 0 ? document.getElementById(`fav-cat-movies-${idx - 1}`) : document.getElementById(`fav-cat-live-${favLiveCategories.length - 1}`);
                        prev?.focus();
                        prev?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      }
                    }}
                    className={`p-3.5 rounded-2xl bg-zinc-900/80 hover:bg-zinc-850 border border-amber-500/30 hover:border-amber-400 transition cursor-pointer flex items-center justify-between group focus:ring-4 focus:ring-amber-400 focus:outline-none ${
                      layoutMode === 'carousel' ? 'w-64 shrink-0' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                        <Film className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] font-black text-amber-400 uppercase tracking-wider">Filmes</span>
                        <h4 className="text-xs font-bold text-white truncate group-hover:text-amber-300">{cat}</h4>
                        <span className="text-[10px] text-zinc-400">{count} filmes</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      title="Remover categoria dos favoritos"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategoryFavorite('movies', cat);
                      }}
                      className="p-1.5 text-amber-400 hover:text-zinc-500 transition cursor-pointer"
                    >
                      <Star className="w-4 h-4 fill-amber-400" />
                    </button>
                  </div>
                );
              })}

              {/* Series Favorite Categories */}
              {favSeriesCategories.map((cat, idx) => {
                const count = series.filter((s) => s.category === cat).length;
                return (
                  <div
                    key={`fav-cat-series-${cat}`}
                    id={`fav-cat-series-${idx}`}
                    tabIndex={0}
                    role="button"
                    onClick={() => {
                      setActiveTab('series');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setActiveTab('series');
                      } else if (e.key === 'ArrowRight' && idx < favSeriesCategories.length - 1 && layoutMode === 'carousel') {
                        e.preventDefault();
                        const next = document.getElementById(`fav-cat-series-${idx + 1}`);
                        next?.focus();
                        next?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      } else if (e.key === 'ArrowLeft' && layoutMode === 'carousel') {
                        e.preventDefault();
                        const prev = idx > 0 ? document.getElementById(`fav-cat-series-${idx - 1}`) : document.getElementById(`fav-cat-movies-${favMovieCategories.length - 1}`);
                        prev?.focus();
                        prev?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      }
                    }}
                    className={`p-3.5 rounded-2xl bg-zinc-900/80 hover:bg-zinc-850 border border-amber-500/30 hover:border-amber-400 transition cursor-pointer flex items-center justify-between group focus:ring-4 focus:ring-amber-400 focus:outline-none ${
                      layoutMode === 'carousel' ? 'w-64 shrink-0' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0">
                        <Tv2 className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] font-black text-purple-400 uppercase tracking-wider">Séries</span>
                        <h4 className="text-xs font-bold text-white truncate group-hover:text-amber-300">{cat}</h4>
                        <span className="text-[10px] text-zinc-400">{count} séries</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      title="Remover categoria dos favoritos"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategoryFavorite('series', cat);
                      }}
                      className="p-1.5 text-amber-400 hover:text-zinc-500 transition cursor-pointer"
                    >
                      <Star className="w-4 h-4 fill-amber-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 2. Canais ao Vivo Favoritos */}
      {favChannelList.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Tv className="w-4 h-4 text-theme-accent" /> Canais ao Vivo Favoritos ({favChannelList.length})
            </h3>
            {layoutMode === 'carousel' && favChannelList.length > 3 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => scrollContainer(channelsScrollRef, 'left')}
                  className="p-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition cursor-pointer shadow flex items-center justify-center"
                  title="Rolar canais para a esquerda (Controle Remoto ◄)"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollContainer(channelsScrollRef, 'right')}
                  className="p-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition cursor-pointer shadow flex items-center justify-center"
                  title="Rolar canais para a direita (Controle Remoto ►)"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="relative group">
            {layoutMode === 'carousel' && favChannelList.length > 4 && (
              <>
                <button
                  type="button"
                  onClick={() => scrollContainer(channelsScrollRef, 'left')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-r-2xl bg-black/80 hover:bg-zinc-900 text-white border-y border-r border-zinc-700 shadow-2xl transition cursor-pointer hidden md:flex items-center justify-center backdrop-blur-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollContainer(channelsScrollRef, 'right')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-l-2xl bg-black/80 hover:bg-zinc-900 text-white border-y border-l border-zinc-700 shadow-2xl transition cursor-pointer hidden md:flex items-center justify-center backdrop-blur-sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}

            <div
              ref={channelsScrollRef}
              className={
                layoutMode === 'carousel'
                  ? 'flex items-center gap-3 overflow-x-auto pb-2 scrollbar-none scroll-smooth px-1'
                  : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'
              }
            >
              {favChannelList.map((ch, idx) => (
                <div
                  key={ch.id}
                  id={`fav-ch-${idx}`}
                  tabIndex={0}
                  role="button"
                  onClick={() => {
                    setActiveChannelCategory('Favoritos');
                    setCurrentChannel(ch);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setActiveChannelCategory('Favoritos');
                      setCurrentChannel(ch);
                    } else if (e.key === ' ') {
                      e.preventDefault();
                      toggleFavorite(ch.id);
                    } else if (e.key === 'ArrowRight' && idx < favChannelList.length - 1 && layoutMode === 'carousel') {
                      e.preventDefault();
                      const next = document.getElementById(`fav-ch-${idx + 1}`);
                      next?.focus();
                      next?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    } else if (e.key === 'ArrowLeft' && idx > 0 && layoutMode === 'carousel') {
                      e.preventDefault();
                      const prev = document.getElementById(`fav-ch-${idx - 1}`);
                      prev?.focus();
                      prev?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }
                  }}
                  className={`p-3.5 rounded-2xl bg-zinc-900/80 hover:bg-zinc-850 border border-zinc-800 hover:border-theme-accent/50 transition cursor-pointer flex items-center justify-between group focus:ring-4 focus:ring-theme-accent focus:outline-none ${
                    layoutMode === 'carousel' ? 'w-64 shrink-0' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={ch.logo}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="w-10 h-10 rounded-xl object-cover border border-white/10 bg-zinc-800 shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=128';
                      }}
                    />
                    <div className="min-w-0">
                      <span className="text-[10px] font-black text-theme-accent">CH {ch.num}</span>
                      <h4 className="text-xs font-bold text-white truncate group-hover:text-theme-accent-light">
                        {ch.name}
                      </h4>
                      <span className="text-[10px] text-zinc-400 truncate block">{ch.groupTitle}</span>
                    </div>
                  </div>

                  <div
                    aria-hidden="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(ch.id);
                    }}
                    className="p-1.5 text-amber-400 hover:text-zinc-400 transition cursor-pointer shrink-0"
                  >
                    <Star className="w-4 h-4 fill-amber-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 3. Filmes Favoritos */}
      {favMovieList.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Film className="w-4 h-4 text-theme-accent" /> Filmes Favoritos ({favMovieList.length})
            </h3>
            {layoutMode === 'carousel' && favMovieList.length > 4 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => scrollContainer(moviesScrollRef, 'left')}
                  className="p-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition cursor-pointer shadow flex items-center justify-center"
                  title="Rolar filmes para a esquerda (Controle Remoto ◄)"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollContainer(moviesScrollRef, 'right')}
                  className="p-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition cursor-pointer shadow flex items-center justify-center"
                  title="Rolar filmes para a direita (Controle Remoto ►)"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="relative group">
            {layoutMode === 'carousel' && favMovieList.length > 5 && (
              <>
                <button
                  type="button"
                  onClick={() => scrollContainer(moviesScrollRef, 'left')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-r-2xl bg-black/80 hover:bg-zinc-900 text-white border-y border-r border-zinc-700 shadow-2xl transition cursor-pointer hidden md:flex items-center justify-center backdrop-blur-sm"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollContainer(moviesScrollRef, 'right')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-l-2xl bg-black/80 hover:bg-zinc-900 text-white border-y border-l border-zinc-700 shadow-2xl transition cursor-pointer hidden md:flex items-center justify-center backdrop-blur-sm"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            <div
              ref={moviesScrollRef}
              className={
                layoutMode === 'carousel'
                  ? 'flex items-center gap-3.5 overflow-x-auto pb-2 scrollbar-none scroll-smooth px-1'
                  : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3.5'
              }
            >
              {favMovieList.map((m, idx) => (
                <div
                  key={m.id}
                  id={`fav-mov-${idx}`}
                  tabIndex={0}
                  role="button"
                  onClick={() => {
                    setActiveMovieCategory('Favoritos');
                    setCurrentMovie(m);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setActiveMovieCategory('Favoritos');
                      setCurrentMovie(m);
                    } else if (e.key === ' ') {
                      e.preventDefault();
                      toggleFavorite(m.id);
                    } else if (e.key === 'ArrowRight' && idx < favMovieList.length - 1 && layoutMode === 'carousel') {
                      e.preventDefault();
                      const next = document.getElementById(`fav-mov-${idx + 1}`);
                      next?.focus();
                      next?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    } else if (e.key === 'ArrowLeft' && idx > 0 && layoutMode === 'carousel') {
                      e.preventDefault();
                      const prev = document.getElementById(`fav-mov-${idx - 1}`);
                      prev?.focus();
                      prev?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }
                  }}
                  className={`group relative rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-theme-accent/50 transition cursor-pointer flex flex-col focus:ring-4 focus:ring-theme-accent focus:outline-none ${
                    layoutMode === 'carousel' ? 'w-40 sm:w-44 shrink-0' : ''
                  }`}
                >
                  <div className="aspect-[2/3] w-full relative">
                    <img src={m.poster} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                      <Play className="w-8 h-8 fill-white text-white" />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(m.id);
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 text-amber-400 hover:scale-110 transition z-10"
                      title="Remover dos favoritos"
                    >
                      <Star className="w-3.5 h-3.5 fill-amber-400" />
                    </button>
                  </div>
                  <div className="p-2.5">
                    <h4 className="text-xs font-bold text-white truncate group-hover:text-theme-accent-light">{m.title}</h4>
                    {getDisplayYear(m.title, m.year) && (
                      <span className="text-[10px] text-zinc-400">{getDisplayYear(m.title, m.year)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 4. Séries Favoritas */}
      {favSeriesList.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Tv2 className="w-4 h-4 text-theme-accent" /> Séries Favoritas ({favSeriesList.length})
            </h3>
            {layoutMode === 'carousel' && favSeriesList.length > 4 && (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => scrollContainer(seriesScrollRef, 'left')}
                  className="p-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition cursor-pointer shadow flex items-center justify-center"
                  title="Rolar séries para a esquerda (Controle Remoto ◄)"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollContainer(seriesScrollRef, 'right')}
                  className="p-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 transition cursor-pointer shadow flex items-center justify-center"
                  title="Rolar séries para a direita (Controle Remoto ►)"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="relative group">
            {layoutMode === 'carousel' && favSeriesList.length > 5 && (
              <>
                <button
                  type="button"
                  onClick={() => scrollContainer(seriesScrollRef, 'left')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-r-2xl bg-black/80 hover:bg-zinc-900 text-white border-y border-r border-zinc-700 shadow-2xl transition cursor-pointer hidden md:flex items-center justify-center backdrop-blur-sm"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollContainer(seriesScrollRef, 'right')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-l-2xl bg-black/80 hover:bg-zinc-900 text-white border-y border-l border-zinc-700 shadow-2xl transition cursor-pointer hidden md:flex items-center justify-center backdrop-blur-sm"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            <div
              ref={seriesScrollRef}
              className={
                layoutMode === 'carousel'
                  ? 'flex items-center gap-3.5 overflow-x-auto pb-2 scrollbar-none scroll-smooth px-1'
                  : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3.5'
              }
            >
              {favSeriesList.map((s, idx) => (
                <div
                  key={s.id}
                  id={`fav-ser-${idx}`}
                  tabIndex={0}
                  role="button"
                  onClick={() => {
                    setActiveSeriesDetail(s);
                    setActiveTab('series');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setActiveSeriesDetail(s);
                      setActiveTab('series');
                    } else if (e.key === ' ') {
                      e.preventDefault();
                      toggleFavorite(s.id);
                    } else if (e.key === 'ArrowRight' && idx < favSeriesList.length - 1 && layoutMode === 'carousel') {
                      e.preventDefault();
                      const next = document.getElementById(`fav-ser-${idx + 1}`);
                      next?.focus();
                      next?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    } else if (e.key === 'ArrowLeft' && idx > 0 && layoutMode === 'carousel') {
                      e.preventDefault();
                      const prev = document.getElementById(`fav-ser-${idx - 1}`);
                      prev?.focus();
                      prev?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }
                  }}
                  className={`group relative rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-theme-accent/50 transition cursor-pointer flex flex-col focus:ring-4 focus:ring-theme-accent focus:outline-none ${
                    layoutMode === 'carousel' ? 'w-40 sm:w-44 shrink-0' : ''
                  }`}
                >
                  <div className="aspect-[2/3] w-full relative">
                    <SeriesPoster
                      poster={s.poster}
                      title={s.title}
                      category={s.category}
                      year={s.year}
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center z-10">
                      <Play className="w-8 h-8 fill-white text-white" />
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(s.id);
                      }}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 text-amber-400 hover:scale-110 transition z-20"
                      title="Remover dos favoritos"
                    >
                      <Star className="w-3.5 h-3.5 fill-amber-400" />
                    </button>
                  </div>
                  <div className="p-2.5">
                    <h4 className="text-xs font-bold text-white truncate group-hover:text-theme-accent-light">{s.title}</h4>
                    <span className="text-[10px] text-zinc-400">{s.seasonsCount} Temp</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
